import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { SCENES } from "@/lib/content";
import { useMediaQuery } from "@/lib/site";
import { Words } from "@/components/Kinetic";
import { ProgressCtx } from "@/components/illustrations/kit";
import { SCENE_ART } from "@/components/illustrations/Scenes";

const ScrubWord = ({ p, i, w, cls }) => {
  const a = 0.02 + i * 0.035;
  const y = useTransform(p, [a, a + 0.12], ["105%", "0%"], { clamp: true });
  const opacity = useTransform(p, [a, a + 0.08], [0, 1], { clamp: true });
  return (
    <motion.span style={{ y, opacity }} className={`inline-block ${cls}`}>
      {w}
    </motion.span>
  );
};

const SceneText = ({ scene, index, p }) => {
  const eyebrow = useTransform(p, [0, 0.08], [0, 1], { clamp: true });
  const num = useTransform(p, [0, 1], [50, -50]);
  const numO = useTransform(p, [0, 0.15], [0, 1], { clamp: true });
  const bar = useTransform(p, [0.05, 0.8], [0, 1], { clamp: true });
  return (
    <div className="relative">
      <motion.span
        aria-hidden="true"
        style={{ y: num, opacity: numO, WebkitTextStroke: "1.5px #D5DDD8" }}
        className="pointer-events-none absolute -top-6 right-0 select-none font-display text-[88px] font-black leading-none text-transparent sm:-top-20 sm:left-0 sm:right-auto sm:text-[170px]"
      >
        {scene.n}
      </motion.span>
      <div className="relative">
        <motion.span style={{ opacity: eyebrow }} className="eyebrow">
          SCENE {scene.n} — {scene.label}
        </motion.span>
        <h3 data-testid={`story-scene-${index + 1}-text`} className="display mt-4 text-[30px] leading-[0.98] text-ink sm:mt-5 sm:text-5xl lg:text-[44px] xl:text-[56px]">
          <Words words={scene.text.split(" ")} hl={scene.hl} render={(w, i, cls) => <ScrubWord p={p} i={i} w={w} cls={cls} />} />
        </h3>
        <div className="mt-5 flex items-center gap-3 sm:mt-8" aria-hidden="true">
          <span className="font-mono text-[11px] text-muted">{scene.n}/04</span>
          <span className="relative h-[3px] w-28 overflow-hidden rounded-full bg-line">
            <motion.span style={{ scaleX: bar }} className="absolute inset-0 origin-left rounded-full bg-emerald" />
          </span>
        </div>
      </div>
    </div>
  );
};

const Scene = ({ scene, index, reduced, lite }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.7", "end end"] });
  const smooth = useSpring(scrollYProgress, { stiffness: 140, damping: 28, mass: 0.35, restDelta: 0.0005 });
  const still = useMotionValue(1);
  const p = reduced ? still : smooth;
  const ArtC = SCENE_ART[index];
  const flip = index % 2 === 1;

  return (
    <ProgressCtx.Provider value={p}>
      <article
        ref={ref}
        data-testid={`story-scene-${index + 1}`}
        className={`relative ${reduced ? "py-14" : "h-[175vh] lg:h-[230vh]"}`}
      >
        <div className={reduced ? "" : "story-pin sticky top-0 flex items-center overflow-hidden"}>
          <div className="wrap grid w-full grid-cols-1 items-center gap-6 pt-[68px] sm:gap-10 lg:grid-cols-12 lg:gap-16 lg:pt-10">
            <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
              <SceneText scene={scene} index={index} p={p} />
            </div>
            <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
              <div className="mx-auto max-w-[460px] overflow-hidden rounded-[24px] bg-white p-2 shadow-soft ring-1 ring-line sm:max-w-none sm:rounded-[32px] sm:p-5">
                <ArtC lite={lite} />
              </div>
            </div>
          </div>
        </div>
      </article>
    </ProgressCtx.Provider>
  );
};

export default function Story() {
  const reduced = useMediaQuery("(prefers-reduced-motion: reduce)");
  const lite = useMediaQuery("(max-width: 767px)");
  return (
    <section id="story" aria-label="The FAIZ story" data-testid="story-section" className="relative bg-soft pt-20 lg:pt-32">
      <div className="wrap">
        <div className="flex items-end justify-between border-b border-line pb-6">
          <span className="eyebrow">THE STORY</span>
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted">04 scenes · scroll</span>
        </div>
      </div>
      {SCENES.map((s, i) => (
        <Scene key={s.n} scene={s} index={i} reduced={reduced} lite={lite} />
      ))}
    </section>
  );
}
